import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { differenceInMinutes, differenceInHours, format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { ArrowLeft, BellRing, Calendar, User } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const PLATFORM_LABEL = {
  tiktok: '틱톡', instagram: '인스타그램', youtube: '유튜브', naver: '네이버 쇼핑라이브',
  kakao: '카카오 쇼핑라이브', grip: '그립', clickmate: '클릭메이트', others: '기타',
};
const CATEGORY_LABEL = {
  fashion: '패션', beauty: '뷰티', food: '식품', pet: '펫', kids: '키즈', living: '리빙',
  electronics: '전자제품', home: '홈', health: '헬스', sports: '스포츠', others: '기타',
};

const humanCountdown = (start, now = new Date()) => {
  const mins = differenceInMinutes(start, now);
  if (mins <= 0) return '곧 시작';
  if (mins <= 60) return `${mins}분 후 시작`;
  const hrs = differenceInHours(start, now);
  if (hrs <= 24) return `${hrs}시간 후 시작`;
  return `${Math.round(hrs / 24)}일 후 시작`;
};

const BroadcastWaiting = () => {
  const { broadcastId } = useParams();
  const navigate = useNavigate();
  const [live, setLive] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/broadcasts/${broadcastId}`);
        if (!cancelled) setLive(res.data);
      } catch { if (!cancelled) setNotFound(true); }
    })();
    const t = setInterval(() => setNow(new Date()), 30 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [broadcastId]);

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="broadcast-waiting-notfound">
        <p className="text-2xl font-black mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>방송을 찾을 수 없습니다</p>
        <Link to="/schedule" className="text-sm text-[#FF3B30] font-bold hover:underline">전체 편성표 보기</Link>
      </div>
    );
  }
  if (!live) return <div className="max-w-2xl mx-auto px-4 py-24 text-sm text-gray-400 text-center">불러오는 중…</div>;

  const start = new Date(live.scheduled_at);
  const sellerName = live.seller?.name || live.seller_name;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="broadcast-waiting-page">
      <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs sm:text-sm text-gray-500 hover:text-black dark:hover:text-white mb-6" data-testid="waiting-back">
        <ArrowLeft size={14} /> 뒤로
      </button>

      <div className="text-center mb-8" data-testid="waiting-status">
        <span className="inline-block px-3 py-1 rounded-full bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-black tracking-widest uppercase">Waiting</span>
        <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>아직 방송 전입니다</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400" data-testid="waiting-countdown">{humanCountdown(start, now)}</p>
      </div>

      <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 mb-6" data-testid="waiting-thumb">
        <SafeImg src={live.home_schedule_image_url || live.thumbnail} kind="broadcast" alt={live.title} className="w-full h-full object-cover" />
      </div>

      <div className="space-y-1 mb-6">
        <p className="text-2xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="waiting-title">{live.title}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="waiting-seller">{sellerName}</p>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <div className="p-4 bg-black/[0.03] dark:bg-white/[0.05] rounded-xl">
          <dt className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">예정 시간</dt>
          <dd className="text-sm font-black" data-testid="waiting-scheduled">{format(start, 'M월 d일 HH:mm', { locale: ko })}</dd>
        </div>
        <div className="p-4 bg-black/[0.03] dark:bg-white/[0.05] rounded-xl">
          <dt className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">남은 시간</dt>
          <dd className="text-sm font-black">{humanCountdown(start, now)}</dd>
        </div>
        <div className="p-4 bg-black/[0.03] dark:bg-white/[0.05] rounded-xl">
          <dt className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">플랫폼</dt>
          <dd className="text-sm font-black">{PLATFORM_LABEL[live.platform] || live.platform}</dd>
        </div>
        <div className="p-4 bg-black/[0.03] dark:bg-white/[0.05] rounded-xl">
          <dt className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">카테고리</dt>
          <dd className="text-sm font-black">{CATEGORY_LABEL[live.category] || live.category}</dd>
        </div>
      </dl>

      <div className="flex flex-col sm:flex-row gap-3">
        <Link to={`/seller/${live.seller_id}`} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-black dark:bg-white text-white dark:text-black text-sm font-bold hover:bg-[#FF3B30] hover:text-white transition-colors" data-testid="waiting-goto-seller">
          <User size={14} /> 셀러 프로필 가기
        </Link>
        <button type="button" onClick={() => toast.success('알림 신청이 완료되었습니다.')} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-black/10 dark:border-white/10 text-sm font-bold hover:border-[#FF3B30] hover:text-[#FF3B30] transition-colors" data-testid="waiting-notify">
          <BellRing size={14} /> 알림 받기
        </button>
        <Link to="/schedule" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FF3B30] hover:bg-[#D32F2F] text-white text-sm font-bold" data-testid="waiting-schedule">
          <Calendar size={14} /> 전체 편성표 보기
        </Link>
      </div>
    </div>
  );
};

export default BroadcastWaiting;
